import React, { useEffect, useMemo, useState } from 'react';
import type { QuoteStatus, QuoteSummary } from '@cortex/domain';
import type { CurrentPolicyContext } from '../../types';
import { CURRENCY_SYMBOLS } from '../../constants';
import { useToast } from '../ui/Toast';
import { cortexApi, isApiConfigured } from '../../services/cortexApi';
import { buildCsv, downloadCsv, type CsvColumn } from '../../utils/csv';
import { StatusBadge } from '../ui/StatusBadge';
import { QUOTE_STATUSES, quoteStatusColorVar, toneForQuoteStatus } from '../ui/quoteStatus';
import { DataTable, DataTableBody, DataTableHead, DataTableHeader } from '../ui/DataTable';

interface QuoteHistoryProps {
  onLoad: (ctx: CurrentPolicyContext) => void;
  onBack: () => void;
}

interface KpiCardProps {
  label: string;
  value: string;
  subtext: string;
  trend?: 'up' | 'down' | 'neutral';
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, subtext, trend = 'neutral' }) => (
  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-start justify-between relative overflow-hidden">
    <div className="relative z-10">
      <p className="text-micro font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <h3 className="text-xl font-black text-slate-800 tracking-tight mb-1">{value}</h3>
      <p
        className={`text-micro font-bold ${
          trend === 'up'
            ? 'text-emerald-600'
            : trend === 'down'
              ? 'text-rose-500'
              : 'text-slate-400'
        }`}
      >
        {subtext}
      </p>
    </div>
  </div>
);

const PipelineBarChart: React.FC<{ data: Array<{ month: string; amount: number }> }> = ({
  data,
}) => {
  const max = Math.max(...data.map((d) => d.amount), 1);
  return (
    <div className="h-32 flex items-end justify-between gap-2">
      {data.map((d) => (
        <div key={d.month} className="flex flex-col items-center gap-1 flex-1 group">
          <div className="relative w-full flex justify-center">
            <div
              className="w-full bg-indigo-100 rounded-t-sm group-hover:bg-hr-pink transition-colors relative overflow-hidden"
              style={{ height: `${(d.amount / max) * 100}px` }}
            />
          </div>
          <span className="text-micro font-bold text-slate-400 uppercase">{d.month}</span>
        </div>
      ))}
    </div>
  );
};

const StatusDonut: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  let accumulated = 0;

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-32 h-32 flex-shrink-0">
        <svg viewBox="0 0 32 32" className="-rotate-90">
          {Object.entries(counts).map(([status, count]) => {
            const pct = count / total;
            const dashArray = `${pct * 100} 100`;
            const offset = -accumulated * 100;
            accumulated += pct;
            return (
              <circle
                key={status}
                cx="16"
                cy="16"
                r="16"
                fill="transparent"
                stroke={quoteStatusColorVar(status as QuoteStatus)}
                strokeWidth="8"
                strokeDasharray={dashArray}
                strokeDashoffset={offset}
              />
            );
          })}
          <circle cx="16" cy="16" r="9" fill="white" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-black text-slate-700">
            {total === 1 && Object.values(counts).every((c) => c === 0) ? 0 : total}
          </span>
          <span className="text-micro uppercase font-bold text-slate-400">Total</span>
        </div>
      </div>
      <div className="space-y-2 flex-1">
        {Object.entries(counts).map(([status, count]) => (
          <div key={status} className="flex justify-between items-center text-micro">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: quoteStatusColorVar(status as QuoteStatus) }}
              />
              <span className="font-bold text-slate-600">{status}</span>
            </div>
            <span className="font-mono text-slate-500">
              {count} ({total > 0 ? Math.round((count / total) * 100) : 0}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const QuoteHistory: React.FC<QuoteHistoryProps> = ({ onLoad, onBack }) => {
  const { showToast } = useToast();
  const [history, setHistory] = useState<QuoteSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isApiConfigured()) return;
    setIsLoading(true);
    void cortexApi.quotes
      .listQuotes()
      .then((res) => setHistory(res.quotes))
      .catch(() => showToast('Failed to load quote history', 'error'))
      .finally(() => setIsLoading(false));
  }, [showToast]);

  const metrics = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 5);

    let totalGross = 0;
    let boundGross = 0;
    let weightedPipeline = 0;
    const statusCounts: Record<string, number> = {
      Draft: 0,
      Quoted: 0,
      Bound: 0,
      Referred: 0,
      Expired: 0,
    };
    const monthlyVolume: Record<string, number> = {};
    const brokerVolume: Record<string, number> = {};

    for (let i = 0; i < 6; i += 1) {
      const d = new Date();
      d.setMonth(now.getMonth() - i);
      const key = d.toLocaleString('default', { month: 'short' });
      monthlyVolume[key] = 0;
    }

    history.forEach((q) => {
      const gwp = parseFloat(q.grossPremium) || 0;
      const status = q.status;

      totalGross += gwp;
      if (status === 'Bound') boundGross += gwp;
      const prob = status === 'Bound' ? 1.0 : status === 'Quoted' ? 0.6 : 0.1;
      weightedPipeline += gwp * prob;

      statusCounts[status] = (statusCounts[status] ?? 0) + 1;

      const qDate = new Date(q.createdAt);
      if (qDate >= sixMonthsAgo) {
        const monthKey = qDate.toLocaleString('default', { month: 'short' });
        if (monthlyVolume[monthKey] !== undefined) monthlyVolume[monthKey] += gwp;
      }

      const broker = q.brokerDisplayName || q.createdByEntityName || 'Direct';
      brokerVolume[broker] = (brokerVolume[broker] ?? 0) + gwp;
    });

    const orderedChartData: Array<{ month: string; amount: number }> = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date();
      d.setMonth(now.getMonth() - i);
      const key = d.toLocaleString('default', { month: 'short' });
      orderedChartData.push({ month: key, amount: monthlyVolume[key] ?? 0 });
    }

    const topBrokers = Object.entries(brokerVolume)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    const bound = statusCounts['Bound'] ?? 0;
    const conversionRate = history.length > 0 ? (bound / history.length) * 100 : 0;
    const avgDeal = history.length > 0 ? totalGross / history.length : 0;

    return {
      totalGross,
      boundGross,
      weightedPipeline,
      statusCounts,
      orderedChartData,
      topBrokers,
      conversionRate,
      avgDeal,
    };
  }, [history]);

  const filteredHistory = history.filter((item) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      item.insuredName.toLowerCase().includes(term) ||
      item.ref.toLowerCase().includes(term) ||
      item.brokerDisplayName.toLowerCase().includes(term);
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleSelectAll = (): void => {
    if (selectedIds.size === filteredHistory.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredHistory.map((item) => item.ref)));
    }
  };

  const toggleSelectOne = (ref: string): void => {
    const newSet = new Set(selectedIds);
    if (newSet.has(ref)) newSet.delete(ref);
    else newSet.add(ref);
    setSelectedIds(newSet);
  };

  const handleDataExport = (): void => {
    const quotesToExport =
      selectedIds.size > 0 ? history.filter((h) => selectedIds.has(h.ref)) : filteredHistory;

    if (quotesToExport.length === 0) {
      showToast('No data to export', 'error');
      return;
    }

    const columns: CsvColumn<QuoteSummary>[] = [
      { header: 'Reference', accessor: (q) => q.ref },
      { header: 'Client', accessor: (q) => q.insuredName },
      { header: 'Broker', accessor: (q) => q.brokerDisplayName },
      { header: 'Premium (Gross)', accessor: (q) => q.grossPremium },
      { header: 'Currency', accessor: (q) => q.currency },
      { header: 'Status', accessor: (q) => q.status },
      { header: 'Created', accessor: (q) => q.createdAt },
    ];

    const filename = `CORTEX_Pipeline_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(filename, buildCsv(quotesToExport, columns));
    showToast(`Exported ${quotesToExport.length} records`, 'success');
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 font-sans text-slate-900">
      <header className="bg-hr-indigo text-white p-4 sticky top-0 z-50 shadow-md">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div>
              <h1 className="font-black text-lg tracking-tight leading-none">Pipeline Manager</h1>
              <span className="text-micro font-bold text-hr-pink uppercase tracking-widest">
                Analytics &amp; History
              </span>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div className="flex gap-2" role="tablist">
              {['All', ...QUOTE_STATUSES].map((s) => (
                <button
                  type="button"
                  key={s}
                  role="tab"
                  aria-selected={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 text-micro font-bold rounded-full transition-colors ${
                    statusFilter === s
                      ? 'bg-white text-hr-indigo'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <input
              type="search"
              aria-label="Search pipeline"
              placeholder="Search pipeline..."
              className="bg-indigo-900/50 border border-indigo-700 text-xs rounded-lg px-4 py-2 text-white placeholder-indigo-300 focus:outline-none focus:ring-1 focus:ring-hr-pink w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              onClick={onBack}
              className="bg-hr-pink hover:bg-hr-pink-hover text-white px-4 py-2 rounded-lg font-bold text-micro transition-colors"
            >
              Exit CRM
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KpiCard
              label="Weighted Pipeline"
              value={`R ${(metrics.weightedPipeline / 1_000_000).toFixed(2)}M`}
              subtext="Forecast Value"
              trend="up"
            />
            <KpiCard
              label="Bound GWP (YTD)"
              value={`R ${(metrics.boundGross / 1_000_000).toFixed(2)}M`}
              subtext={`${metrics.statusCounts['Bound'] ?? 0} Policies Bound`}
              trend="up"
            />
            <KpiCard
              label="Conversion Rate"
              value={`${metrics.conversionRate.toFixed(1)}%`}
              subtext="Quote to Bind Ratio"
            />
            <KpiCard
              label="Avg. Deal Size"
              value={`R ${(metrics.avgDeal / 1000).toFixed(1)}k`}
              subtext="Per Policy Annum"
            />
          </div>

          <div className="xl:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <h4 className="text-xs font-bold text-slate-800 mb-6 flex justify-between">
              <span>Revenue Trend (6 Months)</span>
              <span className="text-micro text-slate-400 font-normal">Gross Premium Quoted</span>
            </h4>
            <PipelineBarChart data={metrics.orderedChartData} />
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
            <h4 className="text-xs font-bold text-slate-800 mb-4">Pipeline Composition</h4>
            <StatusDonut counts={metrics.statusCounts} />
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <h4 className="text-xs font-bold text-slate-800 mb-4">Top Distributors</h4>
            <div className="space-y-4">
              {metrics.topBrokers.map(([name, val], i) => (
                <div key={name} className="flex items-center gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-micro font-bold text-white ${
                      i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-slate-400' : 'bg-orange-400'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-micro font-bold text-slate-700">
                      <span>{name}</span>
                      <span>R {(val / 1000).toFixed(0)}k</span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full mt-1 overflow-hidden">
                      <div
                        className="bg-hr-indigo h-full"
                        style={{
                          width: `${metrics.totalGross > 0 ? (val / metrics.totalGross) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {metrics.topBrokers.length === 0 && (
                <p className="text-micro text-slate-400 italic">No broker data available.</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-xs text-slate-700 uppercase tracking-wide">
                Detailed Pipeline
              </h3>
              <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-micro font-bold">
                {filteredHistory.length}
              </span>
            </div>
            <button
              type="button"
              onClick={handleDataExport}
              disabled={filteredHistory.length === 0}
              className="text-micro font-bold text-hr-indigo hover:text-hr-pink transition-colors disabled:opacity-50"
            >
              Export Data
            </button>
          </div>

          <DataTable>
            <DataTableHead>
              <DataTableHeader align="center" className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  className="rounded text-hr-pink focus:ring-hr-pink"
                  checked={
                    selectedIds.size === filteredHistory.length && filteredHistory.length > 0
                  }
                  onChange={toggleSelectAll}
                />
              </DataTableHeader>
              <DataTableHeader className="px-6 py-3">Opportunity</DataTableHeader>
              <DataTableHeader className="px-6 py-3">Stage</DataTableHeader>
              <DataTableHeader align="right" className="px-6 py-3">
                Premium (Annual)
              </DataTableHeader>
              <DataTableHeader align="right" className="px-6 py-3">
                Prob. Weighted
              </DataTableHeader>
              <DataTableHeader align="right" className="px-6 py-3">
                Action
              </DataTableHeader>
            </DataTableHead>
            <DataTableBody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">
                    Loading quotes…
                  </td>
                </tr>
              )}
              {!isLoading &&
                filteredHistory.map((q) => {
                  const status = q.status;
                  const gwp = parseFloat(q.grossPremium) || 0;
                  const prob = status === 'Bound' ? 100 : status === 'Quoted' ? 60 : 10;
                  const weightedVal = gwp * (prob / 100);
                  const currencySymbol =
                    CURRENCY_SYMBOLS[q.currency as keyof typeof CURRENCY_SYMBOLS] ?? q.currency;

                  return (
                    <tr
                      key={q.ref}
                      className={`hover:bg-indigo-50/30 transition-colors group ${
                        selectedIds.has(q.ref) ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          aria-label={`Select ${q.ref}`}
                          className="rounded text-hr-pink focus:ring-hr-pink"
                          checked={selectedIds.has(q.ref)}
                          onChange={() => toggleSelectOne(q.ref)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-800">
                            {q.insuredName || 'Unknown Prospect'}
                          </span>
                          <span className="text-micro text-slate-500">
                            {q.ref} • {q.brokerDisplayName || q.createdByEntityName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge tone={toneForQuoteStatus(status)} label={status} />
                        <div className="text-micro text-slate-400 mt-1 pl-1">
                          {new Date(q.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-bold text-slate-700">
                          {currencySymbol}{' '}
                          {gwp.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-bold text-slate-500">
                            {currencySymbol}{' '}
                            {weightedVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                          <span className="text-micro text-slate-400">{prob}% Prob.</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            void cortexApi.quotes
                              .getQuote(q.ref)
                              .then((res) => {
                                onLoad(res.quote.context as unknown as CurrentPolicyContext);
                              })
                              .catch(() => showToast('Failed to load quote', 'error'));
                          }}
                          className="text-micro font-bold text-hr-indigo hover:text-hr-pink transition-colors"
                        >
                          Open Record
                        </button>
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && filteredHistory.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400 italic">
                    {history.length === 0
                      ? 'No quotes saved yet. Calculate a quote to see it here.'
                      : 'No records match your filters'}
                  </td>
                </tr>
              )}
            </DataTableBody>
          </DataTable>
        </div>
      </main>
    </div>
  );
};
